import { useEffect, useState, useCallback } from 'react';
import { CheckCircle, Clock, RefreshCw, AlertTriangle, ShieldAlert, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface AbuseReport {
  id: string; address: string | null; message_id: string | null; reason: string | null;
  severity: string | null; reporter_ip_hash: string | null; reported_at: number;
  resolved: number; resolved_at: number | null; notes: string | null;
}

const SEVERITY_CONFIG: Record<string, { label: string; color: string }> = {
  low:      { label: 'Low',      color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  medium:   { label: 'Medium',   color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  high:     { label: 'High',     color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-bold' },
};

const PAGE = 25;

export default function AbuseReports() {
  const [reports, setReports] = useState<AbuseReport[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<'all' | 'open' | 'resolved'>('open');

  const load = useCallback(async () => {
    setLoading(true);
    const offset = (page - 1) * PAGE;
    const res = await apiFetch(`/api/admin/abuse-reports?filter=${filter}&limit=${PAGE}&offset=${offset}`);
    if (res.ok) {
      const data = await res.json();
      const rows: AbuseReport[] = data.reports || [];
      setReports(rows);
      setTotal(data.total || 0);
      const initNotes: Record<string, string> = {};
      rows.forEach(r => { initNotes[r.id] = r.notes || ''; });
      setNotes(prev => ({ ...initNotes, ...prev }));
    }
    setLoading(false);
  }, [filter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter]);

  const update = async (r: AbuseReport, patch: Partial<{ resolved: boolean; notes: string; severity: string }>) => {
    setSaving(r.id);
    await apiFetch('/api/admin/abuse-reports', {
      method: 'PATCH',
      body: JSON.stringify({ id: r.id, resolved: patch.resolved ?? !!r.resolved, notes: patch.notes ?? notes[r.id], severity: patch.severity ?? r.severity ?? 'medium' }),
    });
    setSaving(null);
    load();
  };

  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">Abuse Reports</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {total.toLocaleString()} {filter !== 'all' ? `${filter} ` : ''}reports
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600">
            {(['open', 'resolved', 'all'] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-sm font-medium capitalize transition-colors ${filter === f ? 'bg-red-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                {f}
              </button>
            ))}
          </div>
          <button onClick={load} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {loading && reports.length === 0 ? (
        <div className="py-16 text-center text-gray-400"><RefreshCw size={20} className="animate-spin inline mr-2" />Loading…</div>
      ) : reports.length === 0 ? (
        <div className="py-16 text-center text-gray-400">
          <ShieldAlert size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">No {filter !== 'all' ? `${filter} ` : ''}reports found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map(r => (
            <div key={r.id}
              className={`bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg border p-6 transition ${r.resolved ? 'border-green-200/60 dark:border-green-800/40 opacity-75' : 'border-red-200/60 dark:border-red-800/60'}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0 space-y-1.5">
                  {/* Status + severity */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {r.resolved
                      ? <CheckCircle size={15} className="text-green-500 shrink-0" />
                      : <Clock size={15} className="text-orange-500 shrink-0" />}
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 font-mono">
                      {r.address || 'Unknown address'}
                    </span>
                    {/* Severity badge */}
                    {(() => {
                      const sev = r.severity || 'medium';
                      const cfg = SEVERITY_CONFIG[sev] || SEVERITY_CONFIG.medium;
                      return (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.color} flex items-center gap-1`}>
                          <AlertTriangle size={10} /> {cfg.label}
                        </span>
                      );
                    })()}
                    {r.message_id && <span className="text-xs text-gray-400 font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">{r.message_id.slice(0, 16)}…</span>}
                  </div>
                  {r.reason && <p className="text-sm text-gray-600 dark:text-gray-300 italic">"{r.reason}"</p>}
                  <p className="text-xs text-gray-400">
                    Reported {new Date(r.reported_at * 1000).toLocaleString()}
                    {r.reporter_ip_hash && <span className="ml-2 font-mono">IP: {r.reporter_ip_hash}</span>}
                    {r.resolved && r.resolved_at && <span className="ml-2 text-green-500"> · Resolved {new Date(r.resolved_at * 1000).toLocaleString()}</span>}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  {/* Severity selector */}
                  <select
                    value={r.severity || 'medium'}
                    onChange={e => update(r, { severity: e.target.value })}
                    disabled={saving === r.id}
                    className="px-2 py-1 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none"
                  >
                    {Object.entries(SEVERITY_CONFIG).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => update(r, { resolved: !r.resolved })}
                    disabled={saving === r.id}
                    className={`px-4 py-1.5 rounded-xl text-sm font-medium transition disabled:opacity-50 ${
                      r.resolved
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
                        : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                    }`}
                  >
                    {saving === r.id ? '…' : r.resolved ? 'Reopen' : 'Resolve'}
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div className="mt-4 flex gap-2">
                <input
                  value={notes[r.id] || ''}
                  onChange={e => setNotes(n => ({ ...n, [r.id]: e.target.value }))}
                  placeholder="Internal notes…"
                  className="flex-1 px-3 py-1.5 text-sm rounded-xl border border-gray-300 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-300"
                />
                <button onClick={() => update(r, { notes: notes[r.id] })} disabled={saving === r.id}
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50">
                  Save
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-6 text-sm text-gray-600 dark:text-gray-400">
          <span>Page <b>{page}</b> of <b>{pages}</b> · {total} total</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">
              <ChevronLeft size={15} /> Prev
            </button>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">
              Next <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
